import { computed } from 'vue'
import { useState } from '#app'
import { useChatThreads } from './useChatThreads'
import { useChatSocket } from './useChatSocket'

export interface StaffRoomMeta {
    threadId: string | null
    title: string
    unread: number
}

/**
 * Composable for the tenant-wide Staff room (admins + label managers).
 *
 * Backed by `GET /api/chat/staff-room/thread` (upsert + hydrate). Entering the
 * room resolves a threadId that the existing DM/group/label thread view can
 * render, so the whole thread renderer is reused. The hydrated row is merged
 * into `useChatThreads` so `ChatThreadView` resolves the header + kind.
 */
// Wire the live-unread listener exactly once (composable may be called many
// times). Module-level, mirrors useChatThreads' `wired` pattern.
let staffUnreadWired = false

export function useChatStaffRoom() {
    const { user } = useUserSession()
    const { upsertRoomThread } = useChatThreads()

    const meta = useState<StaffRoomMeta>('chat-staff-room', () => ({ threadId: null, title: 'Staff', unread: 0 }))
    const loading = useState('chat-staff-room-loading', () => false)
    const entering = useState('chat-staff-room-entering', () => false)

    // Live staff-tab unread badge: the send endpoint pushes `thread.unread` to
    // each staff member's user channel. The DM-list handler in useChatThreads
    // can't drive THIS badge (it reads `staffMeta.unread`, not the DM list), so
    // bump it here when the frame targets the resolved staff thread. Without
    // this the badge only refreshed on staff-tab activation (loadRoom).
    if (import.meta.client && !staffUnreadWired) {
        staffUnreadWired = true
        const { on } = useChatSocket()
        on('thread.unread', (frame: any) => {
            if (!frame || typeof frame.threadId !== 'string') return
            if (!meta.value.threadId || frame.threadId !== meta.value.threadId) return
            meta.value = { ...meta.value, unread: Math.max(0, Number(frame.count) || 0) }
        })
    }

    // Visible to admins + LM tier (formal role or the legacy
    // creator+canCreateLabels shape). Mirrors the backend membership resolver
    // and usePermissions.isLabelManager. The server re-checks on every call,
    // so this only governs whether we render the entry at all.
    const canSee = computed(() => {
        const u = user.value as any
        if (!u) return false
        if (u.role === 'admin') return true
        if (u.role === 'label_manager') return true
        if (u.role === 'creator' && u.canCreateLabels === true && !u.parentCreator) return true
        return false
    })

    async function fetchRoom(): Promise<StaffRoomMeta | null> {
        if (!canSee.value) return null
        try {
            const res = await $fetch<{ thread: any }>('/api/chat/staff-room/thread')
            const t = res?.thread
            if (t?.id) {
                // Seed the room side-map so ChatThreadView resolves the
                // header + kind. Deliberately NOT the DM list (would render
                // as a broken row + get wiped by loadThreads).
                upsertRoomThread(t)
                meta.value = { threadId: t.id, title: t.title || 'Staff', unread: t.unread || 0 }
            }
            return meta.value
        } catch (err) {
            // Chat disabled / demoted / not logged in — stay quiet.
            return null
        }
    }

    /** Load room meta (for the unread badge) without opening it / marking read. */
    async function loadRoom() {
        if (loading.value || !canSee.value) return
        loading.value = true
        try { await fetchRoom() } finally { loading.value = false }
    }

    /** Ensure the room + return its threadId, ready to open in the thread view. */
    async function enterStaff(): Promise<string | null> {
        if (entering.value) return meta.value.threadId
        entering.value = true
        try {
            const m = await fetchRoom()
            return m?.threadId || null
        } finally {
            entering.value = false
        }
    }

    /** Local optimism: clear the unread badge on open. */
    function markStaffRead() {
        meta.value = { ...meta.value, unread: 0 }
    }

    return { meta, canSee, loading, entering, loadRoom, enterStaff, markStaffRead }
}
